import React, { useState, useRef, useEffect } from 'react';
import styles from './Select.module.scss';

function Select({ 
  options = [], 
  value, 
  onChange,
  placeholder = 'Select an option',
  id,
  label,
  className = '',
  disabled = false,
  showSearch = true
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const selectRef = useRef(null);
  const inputRef = useRef(null);
  const optionsRef = useRef(null);
  const optionRefs = useRef({});
  
  // Find the selected option from the options array
  const selectedOption = options.find(option => option.value === value);
  
  // Filter options when user is searching
  const filteredOptions = showSearch 
    ? options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase())) 
    : options;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle closed select keyboard navigation (when dropdown is closed)
  useEffect(() => {
    if (isOpen) return;
    
    function handleKeyDown(e) {
      if (document.activeElement !== selectRef.current) return;
      
      switch (e.key) {
        case ' ':
        case 'Enter':
        case 'ArrowDown':
        case 'ArrowUp':
          e.preventDefault();
          setIsOpen(true);
          break;
        default:
          break;
      }
    }
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Reset option refs when options change
  useEffect(() => {
    optionRefs.current = {};
  }, [filteredOptions]);

  // Handle keyboard navigation inside the dropdown
  useEffect(() => {
    if (!isOpen) return;
    
    function handleKeyDown(e) {
      const focusedElement = document.activeElement;
      const isSearchFocused = focusedElement === inputRef.current;
      const isOptionFocused = focusedElement && focusedElement.getAttribute('role') === 'option';
      
      // Get current highlighted option's index
      let currentIndex = -1;
      if (isOptionFocused) {
        // Find which option is currently focused based on element ID
        const currentId = focusedElement.id;
        currentIndex = filteredOptions.findIndex(
          option => `option-${option.value}` === currentId
        );
        if (currentIndex !== -1) {
          setHighlightedIndex(currentIndex);
        }
      }
      
      // Handle navigation based on what's focused
      if (isSearchFocused) {
        // Search input focused
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            // Move focus to first option
            if (filteredOptions.length > 0) {
              const firstOptionId = `option-${filteredOptions[0].value}`;
              const firstOptionElement = document.getElementById(firstOptionId);
              if (firstOptionElement) {
                firstOptionElement.focus();
                setHighlightedIndex(0);
              }
            }
            break;
          case 'Escape':
            e.preventDefault();
            setIsOpen(false);
            setSearchTerm('');
            selectRef.current?.focus();
            break;
          case 'Tab':
            // Allow tabbing to first option, prevent leaving dropdown
            e.preventDefault();
            if (filteredOptions.length > 0) {
              const firstOptionId = `option-${filteredOptions[0].value}`;
              const firstOptionElement = document.getElementById(firstOptionId);
              if (firstOptionElement) {
                firstOptionElement.focus();
                setHighlightedIndex(0);
              }
            }
            break;
          default:
            break;
        }
      } else if (isOptionFocused) {
        // Option focused
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            if (currentIndex < filteredOptions.length - 1) {
              const nextIndex = currentIndex + 1;
              const nextOptionId = `option-${filteredOptions[nextIndex].value}`;
              const nextOptionElement = document.getElementById(nextOptionId);
              if (nextOptionElement) {
                nextOptionElement.focus();
                setHighlightedIndex(nextIndex);
                nextOptionElement.scrollIntoView({ block: 'nearest' });
              }
            }
            break;
            
          case 'ArrowUp':
            e.preventDefault();
            if (currentIndex > 0) {
              const prevIndex = currentIndex - 1;
              const prevOptionId = `option-${filteredOptions[prevIndex].value}`;
              const prevOptionElement = document.getElementById(prevOptionId);
              if (prevOptionElement) {
                prevOptionElement.focus();
                setHighlightedIndex(prevIndex);
                prevOptionElement.scrollIntoView({ block: 'nearest' });
              }
            } else if (showSearch) {
              // Move focus back to search input if search is enabled
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }
            break;
            
          case 'Enter':
          case ' ':
            e.preventDefault();
            if (currentIndex !== -1) {
              handleSelect(filteredOptions[currentIndex]);
              selectRef.current?.focus();
            }
            break;
            
          case 'Escape':
            e.preventDefault();
            setIsOpen(false);
            setSearchTerm('');
            selectRef.current?.focus();
            break;
            
          case 'Home':
            e.preventDefault();
            if (filteredOptions.length > 0) {
              const firstOptionId = `option-${filteredOptions[0].value}`;
              const firstOptionElement = document.getElementById(firstOptionId);
              if (firstOptionElement) {
                firstOptionElement.focus();
                setHighlightedIndex(0);
                firstOptionElement.scrollIntoView({ block: 'nearest' });
              }
            }
            break;
            
          case 'End':
            e.preventDefault();
            if (filteredOptions.length > 0) {
              const lastIndex = filteredOptions.length - 1;
              const lastOptionId = `option-${filteredOptions[lastIndex].value}`;
              const lastOptionElement = document.getElementById(lastOptionId);
              if (lastOptionElement) {
                lastOptionElement.focus();
                setHighlightedIndex(lastIndex);
                lastOptionElement.scrollIntoView({ block: 'nearest' });
              }
            }
            break;
            
          default:
            // If user types a letter, focus back to search and populate
            if (e.key.length === 1 && e.key.match(/[a-z0-9]/i) && showSearch) {
              if (inputRef.current) {
                inputRef.current.focus();
                setSearchTerm(prev => prev + e.key);
              }
            }
            break;
        }
      }
    }
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, filteredOptions, showSearch]);

  // Reset highlighted index when options change
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
    }
  }, [filteredOptions, isOpen]);

  // Handle toggle dropdown
  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setSearchTerm('');
      
      if (!isOpen && showSearch) {
        // Focus input when opening if search is enabled
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 10);
      }
    }
  };

  // Handle option selection
  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Reset highlighted index when searching
    setHighlightedIndex(0);
  };

  return (
    <div 
      className={`${styles.selectContainer} ${className} ${disabled ? styles.disabled : ''}`}
      ref={containerRef}
    >
      {label && (
        <label 
          htmlFor={id || `select-${label.replace(/\s+/g, '-').toLowerCase()}`}
          className={styles.label}
        >
          {label}
        </label>
      )}
      
      <div 
        className={`${styles.select} ${isOpen ? styles.open : ''}`}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
        ref={selectRef}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={`${id || 'select'}-listbox`}
        aria-label={label || 'Select'}
        tabIndex={disabled ? -1 : 0}
        id={id || `select-${label?.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className={`${styles.selectedValue} ${!selectedOption ? styles.placeholder : ''}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </div>
        <div className={styles.arrow}>
          <svg 
            width="10" 
            height="6" 
            viewBox="0 0 10 6" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className={`${styles.arrowIcon} ${isOpen ? styles.open : ''}`}
            aria-hidden="true"
          >
            <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
      
      {isOpen && (
        <div 
          className={styles.dropdown}
          ref={optionsRef}
        >
          {showSearch && (
            <div className={styles.searchContainer}>
              <input
                ref={inputRef}
                type="text"
                className={styles.search}
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                aria-label="Search options"
                aria-controls={`${id || 'select'}-listbox`}
                aria-activedescendant={
                  highlightedIndex >= 0 && filteredOptions[highlightedIndex]
                    ? `option-${filteredOptions[highlightedIndex].value}`
                    : undefined
                }
              />
            </div>
          )}
          
          <ul 
            className={styles.options}
            role="listbox"
            id={`${id || 'select'}-listbox`}
            aria-label={label || 'Options'}
            tabIndex={-1}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <li
                  key={option.value}
                  id={`option-${option.value}`}
                  className={`
                    ${styles.option} 
                    ${value === option.value ? styles.selected : ''} 
                    ${index === highlightedIndex ? styles.highlighted : ''}
                  `}
                  onClick={() => handleSelect(option)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelect(option);
                      selectRef.current?.focus();
                    }
                  }}
                  role="option"
                  aria-selected={value === option.value}
                  tabIndex={0}
                >
                  {option.label}
                </li>
              ))
            ) : (
              <li className={styles.noOptions}>No options found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default Select; 